/**
 * 模板上下文 - 管理网站模板状态
 * 支持模板切换、颜色配置、登录页风格
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getCurrentSiteId } from '@/utils/api';

// ============================================
// 类型定义
// ============================================

export interface TemplateConfig {
  id: string;
  name: string;
  desc: string;
  color: string;       // 主色调
  bg: string;          // 背景色
  preview: string;     // 预览图
}

export interface LoginStyleConfig {
  id: string;
  name: string;
  desc: string;
  preview: string;
}

export interface TemplateContextType {
  // 当前模板
  currentTemplate: TemplateConfig;
  templateId: string;
  setTemplateId: (id: string) => void;
  
  // 登录页风格
  loginStyle: string;
  setLoginStyle: (id: string) => void;
  
  // 主题颜色
  themeColor: string;
  setThemeColor: (color: string) => void;
  
  // 预定义颜色
  customColors: string[];
  setCustomColors: (colors: string[]) => void;
  
  // 保存配置到服务器
  saveToServer: () => Promise<void>;
  
  // 加载服务器配置
  loadFromServer: () => Promise<void>;
  
  // 是否正在加载
  isLoading: boolean;
}

// ============================================
// 预定义模板配置
// ============================================

export const SITE_TEMPLATES: TemplateConfig[] = [
  {
    id: "classic",
    name: "经典橙色",
    desc: "经典橙色主题，简洁大方",
    color: "#F97316",
    bg: "#FFF7ED",
    preview: "https://readdy.ai/api/search-image?query=clean%20minimalist%20orange%20business%20directory%20website%20template%20with%20white%20background%20and%20orange%20accent%20colors%20modern%20UI%20design%20professional%20layout%20full%20page%20screenshot&width=800&height=500&seq=tpl1v2&orientation=landscape",
  },
  {
    id: "modern",
    name: "现代蓝色",
    desc: "现代蓝色风格，卡片网格布局，清新专业",
    color: "#3B82F6",
    bg: "#EFF6FF",
    preview: "https://readdy.ai/api/search-image?query=modern%20blue%20business%20directory%20website%20template%20card%20grid%20layout%20gradient%20hero%20section%20clean%20professional%20UI%20design%20full%20page%20screenshot&width=800&height=500&seq=tpl-modern&orientation=landscape",
  },
  {
    id: "dark",
    name: "深色商务",
    desc: "暗黑奢华风格，金色点缀，高端商务感",
    color: "#F59E0B",
    bg: "#0F172A",
    preview: "https://readdy.ai/api/search-image?query=dark%20theme%20luxury%20business%20directory%20website%20template%20dark%20background%20gold%20amber%20accent%20modern%20elegant%20UI%20design%20full%20page%20screenshot&width=800&height=500&seq=tpl2v2&orientation=landscape",
  },
  {
    id: "green",
    name: "清新绿意",
    desc: "自然有机设计，绿意清新，充满生机",
    color: "#10B981",
    bg: "#ECFDF5",
    preview: "https://readdy.ai/api/search-image?query=fresh%20green%20organic%20nature%20style%20business%20service%20website%20template%20clean%20white%20background%20emerald%20green%20accent%20modern%20natural%20layout%20full%20page%20screenshot&width=800&height=500&seq=tpl3v2&orientation=landscape",
  },
  {
    id: "red",
    name: "热情红色",
    desc: "红色热情，活力充沛",
    color: "#EF4444",
    bg: "#FEF2F2",
    preview: "https://readdy.ai/api/search-image?query=vibrant%20red%20energetic%20business%20directory%20website%20template%20clean%20white%20background%20red%20accent%20modern%20bold%20UI%20design%20full%20page%20screenshot&width=800&height=500&seq=tpl4v2&orientation=landscape",
  },
  {
    id: "tech-blue",
    name: "科技蓝",
    desc: "清爽SaaS科技风格，蓝白配色，现代简约",
    color: "#2563EB",
    bg: "#EFF6FF",
    preview: "https://readdy.ai/api/search-image?query=modern%20SaaS%20tech%20blue%20business%20directory%20website%20template%20white%20background%20blue%20gradient%20hero%20card%20grid%20clean%20professional%20UI%20full%20page%20screenshot&width=800&height=500&seq=tpl-tech-blue&orientation=landscape",
  },
  {
    id: "deep-blue",
    name: "深邃蓝",
    desc: "深蓝商务风格，沉稳大气，专业可信赖",
    color: "#1E3A5F",
    bg: "#F0F4F8",
    preview: "https://readdy.ai/api/search-image?query=deep%20navy%20blue%20professional%20corporate%20business%20directory%20website%20template%20white%20cards%20blue%20accents%20modern%20clean%20trustworthy%20UI%20full%20page%20screenshot&width=800&height=500&seq=tpl-deep-blue&orientation=landscape",
  },
  {
    id: "teal",
    name: "清新薄荷",
    desc: "青绿薄荷，清新自然，玻璃拟态设计",
    color: "#0D9488",
    bg: "#F0FDFA",
    preview: "https://readdy.ai/api/search-image?query=fresh%20mint%20teal%20green%20business%20directory%20website%20template%20glassmorphism%20clean%20white%20background%20teal%20accent%20modern%20natural%20UI%20full%20page%20screenshot&width=800&height=500&seq=tpl-teal&orientation=landscape",
  },
  {
    id: "sunset",
    name: "日落橙",
    desc: "温暖琥珀橙，日落余晖，柔和亲切",
    color: "#F59E0B",
    bg: "#FFFBF5",
    preview: "https://readdy.ai/api/search-image?query=warm%20sunset%20orange%20amber%20business%20directory%20website%20template%20clean%20white%20warm%20background%20orange%20gradient%20accent%20modern%20friendly%20UI%20full%20page%20screenshot&width=800&height=500&seq=tpl-sunset&orientation=landscape",
  },
  {
    id: "purple",
    name: "极简紫韵",
    desc: "紫色渐变，极简非对称布局，现代艺术感",
    color: "#7C3AED",
    bg: "#F5F3FF",
    preview: "https://readdy.ai/api/search-image?query=minimal%20purple%20violet%20gradient%20business%20directory%20website%20template%20asymmetric%20layout%20clean%20white%20modern%20elegant%20UI%20design%20full%20page%20screenshot&width=800&height=500&seq=tpl-purple&orientation=landscape",
  },
];

export const LOGIN_STYLES: LoginStyleConfig[] = [
  { id: "ls1", name: "居中卡片", desc: "白色卡片居中，简洁大方", preview: "https://readdy.ai/api/search-image?query=centered%20white%20card%20login%20page%20design%20clean%20minimalist%20light%20background%20form%20centered%20modern%20UI&width=240&height=160&seq=ls1&orientation=landscape" },
  { id: "ls2", name: "左图右表单", desc: "左侧大图，右侧登录表单", preview: "https://readdy.ai/api/search-image?query=split%20screen%20login%20page%20left%20image%20right%20form%20modern%20clean%20design%20professional%20business%20website&width=240&height=160&seq=ls2&orientation=landscape" },
  { id: "ls3", name: "全屏背景图", desc: "全屏背景图片，卧式卡片", preview: "https://readdy.ai/api/search-image?query=fullscreen%20background%20image%20login%20page%20overlay%20card%20form%20modern%20elegant%20design%20business%20website&width=240&height=160&seq=ls3&orientation=landscape" },
  { id: "ls4", name: "渐变色背景", desc: "渐变色背景，现代感十足", preview: "https://readdy.ai/api/search-image?query=gradient%20background%20login%20page%20modern%20colorful%20gradient%20white%20card%20form%20clean%20UI%20design&width=240&height=160&seq=ls4&orientation=landscape" },
  { id: "ls5", name: "深色模式", desc: "深色背景，高端大气", preview: "https://readdy.ai/api/search-image?query=dark%20mode%20login%20page%20dark%20background%20white%20card%20form%20elegant%20modern%20professional%20design&width=240&height=160&seq=ls5&orientation=landscape" },
  { id: "ls6", name: "几何图案", desc: "几何图案装饰，艺术感强", preview: "https://readdy.ai/api/search-image?query=geometric%20pattern%20login%20page%20abstract%20shapes%20background%20modern%20artistic%20clean%20form%20design&width=240&height=160&seq=ls6&orientation=landscape" },
  { id: "ls7", name: "插图风格", desc: "可爱插图装饰，清新活泼", preview: "https://readdy.ai/api/search-image?query=illustration%20style%20login%20page%20cute%20flat%20illustration%20decoration%20clean%20white%20form%20modern%20friendly%20design&width=240&height=160&seq=ls7&orientation=landscape" },
  { id: "ls8", name: "极简线条", desc: "极简线条设计，优雅精致", preview: "https://readdy.ai/api/search-image?query=ultra%20minimal%20line%20art%20login%20page%20white%20background%20thin%20lines%20elegant%20minimalist%20clean%20form%20design&width=240&height=160&seq=ls8&orientation=landscape" },
  { id: "ls9", name: "卡通小清新", desc: "卡通风格，轻松活泼", preview: "https://readdy.ai/api/search-image?query=cartoon%20style%20login%20page%20playful%20colorful%20friendly%20clean%20white%20form%20modern%20fun%20design&width=240&height=160&seq=ls9&orientation=landscape" },
  { id: "ls10", name: "商务专业风", desc: "商务风格，专业可信", preview: "https://readdy.ai/api/search-image?query=corporate%20professional%20business%20login%20page%20formal%20clean%20white%20blue%20accent%20trustworthy%20modern%20design&width=240&height=160&seq=ls10&orientation=landscape" },
];

export const PRESET_COLORS = [
  { name: "活力橙", value: "#F97316" },
  { name: "热情红", value: "#EF4444" },
  { name: "玫瑰粉", value: "#F43F5E" },
  { name: "清新绿", value: "#10B981" },
  { name: "森林绿", value: "#16A34A" },
  { name: "天空青", value: "#06B6D4" },
  { name: "深邃灰", value: "#374151" },
  { name: "暖棕色", value: "#92400E" },
  { name: "金黄色", value: "#D97706" },
  { name: "丁香紫", value: "#7C3AED" },
];

// 默认配置
const DEFAULT_TEMPLATE = SITE_TEMPLATES[0];
const DEFAULT_LOGIN_STYLE = "ls1";
const DEFAULT_THEME_COLOR = "#F97316";

// localStorage 键名
const STORAGE_KEYS = {
  TEMPLATE_ID: 'site_template_id',
  LOGIN_STYLE: 'site_login_style',
  THEME_COLOR: 'site_theme_color',
  CUSTOM_COLORS: 'site_custom_colors',
};

// ============================================
// Context
// ============================================

const TemplateContext = createContext<TemplateContextType>({
  currentTemplate: DEFAULT_TEMPLATE,
  templateId: DEFAULT_TEMPLATE.id,
  setTemplateId: () => {},
  loginStyle: DEFAULT_LOGIN_STYLE,
  setLoginStyle: () => {},
  themeColor: DEFAULT_THEME_COLOR,
  setThemeColor: () => {},
  customColors: [],
  setCustomColors: () => {},
  saveToServer: async () => {},
  loadFromServer: async () => {},
  isLoading: false,
});

// ============================================
// Provider
// ============================================

export function TemplateProvider({ children }: { children: ReactNode }) {
  const [templateId, setTemplateIdState] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.TEMPLATE_ID) || DEFAULT_TEMPLATE.id;
  });
  
  const [loginStyle, setLoginStyleState] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.LOGIN_STYLE) || DEFAULT_LOGIN_STYLE;
  });
  
  const [themeColor, setThemeColorState] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.THEME_COLOR) || DEFAULT_THEME_COLOR;
  });
  
  const [customColors, setCustomColorsState] = useState<string[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.CUSTOM_COLORS);
    return stored ? JSON.parse(stored) : [];
  });
  
  const [isLoading, setIsLoading] = useState(false);

  // 获取当前模板配置
  const currentTemplate = SITE_TEMPLATES.find(t => t.id === templateId) || DEFAULT_TEMPLATE;

  // 设置模板ID
  const setTemplateId = useCallback((id: string) => {
    setTemplateIdState(id);
    localStorage.setItem(STORAGE_KEYS.TEMPLATE_ID, id);
    
    // 同时更新主题颜色
    const template = SITE_TEMPLATES.find(t => t.id === id);
    if (template) {
      setThemeColor(template.color);
      localStorage.setItem(STORAGE_KEYS.THEME_COLOR, template.color);
    }
  }, []);

  // 设置登录页风格
  const setLoginStyle = useCallback((id: string) => {
    setLoginStyleState(id);
    localStorage.setItem(STORAGE_KEYS.LOGIN_STYLE, id);
  }, []);

  // 设置主题颜色
  const setThemeColor = useCallback((color: string) => {
    setThemeColorState(color);
    localStorage.setItem(STORAGE_KEYS.THEME_COLOR, color);
  }, []);

  // 设置自定义颜色
  const setCustomColors = useCallback((colors: string[]) => {
    setCustomColorsState(colors);
    localStorage.setItem(STORAGE_KEYS.CUSTOM_COLORS, JSON.stringify(colors));
  }, []);

  // 保存配置到服务器
  const saveToServer = useCallback(async () => {
    const response = await fetch('/api/admin/settings/decoration', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        'X-Site-Id': getCurrentSiteId(),
      },
      body: JSON.stringify({
        template_id: templateId,
        login_style: loginStyle,
        theme_color: themeColor,
        custom_colors: customColors,
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`保存失败 (${response.status}): ${errorText}`);
    }
    
    const result = await response.json();
    if (result.code !== 0) {
      throw new Error(result.message || '保存失败');
    }
  }, [templateId, loginStyle, themeColor, customColors]);

  // 从服务器加载配置（通过公开站点 API，保证跨浏览器一致性）
  const loadFromServer = useCallback(async () => {
    setIsLoading(true);
    try {
      // 通过公开的 by-domain API 获取站点配置（无需认证，跨浏览器可用）
      const domain = window.location.hostname;
      const response = await fetch(`/api/sites/by-domain?domain=${encodeURIComponent(domain)}`);
      if (response.ok) {
        const result = await response.json();
        const data = result.data;
        
        if (data) {
          if (data.template_id) {
            setTemplateIdState(data.template_id);
            localStorage.setItem(STORAGE_KEYS.TEMPLATE_ID, data.template_id);
          }
          
          if (data.theme_color) {
            setThemeColorState(data.theme_color);
            localStorage.setItem(STORAGE_KEYS.THEME_COLOR, data.theme_color);
          }
          
          if (data.login_style) {
            setLoginStyleState(data.login_style);
            localStorage.setItem(STORAGE_KEYS.LOGIN_STYLE, data.login_style);
          }
          
          if (data.custom_colors && Array.isArray(data.custom_colors)) {
            setCustomColorsState(data.custom_colors);
            localStorage.setItem(STORAGE_KEYS.CUSTOM_COLORS, JSON.stringify(data.custom_colors));
          }
        }
      }
    } catch (error) {
      console.error('加载模板配置失败:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 初始化时尝试从服务器加载
  useEffect(() => {
    loadFromServer();
  }, [loadFromServer]);

  // 应用主题颜色到 CSS 变量
  useEffect(() => {
    document.documentElement.style.setProperty('--template-primary', themeColor);
    
    // 根据主题色生成相关颜色
    const lighterColor = adjustColorBrightness(themeColor, 40);
    const darkerColor = adjustColorBrightness(themeColor, -20);
    
    document.documentElement.style.setProperty('--template-primary-light', lighterColor);
    document.documentElement.style.setProperty('--template-primary-dark', darkerColor);
  }, [themeColor]);

  const value: TemplateContextType = {
    currentTemplate,
    templateId,
    setTemplateId,
    loginStyle,
    setLoginStyle,
    themeColor,
    setThemeColor,
    customColors,
    setCustomColors,
    saveToServer,
    loadFromServer,
    isLoading,
  };

  return (
    <TemplateContext.Provider value={value}>
      {children}
    </TemplateContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

export function useTemplate() {
  return useContext(TemplateContext);
}

// ============================================
// 工具函数
// ============================================

/**
 * 调整颜色亮度
 * @param color 颜色值 (#RRGGBB)
 * @param amount 正数变亮，负数变暗
 */
function adjustColorBrightness(color: string, amount: number): string {
  const hex = color.replace('#', '');
  const r = Math.max(0, Math.min(255, parseInt(hex.slice(0, 2), 16) + amount));
  const g = Math.max(0, Math.min(255, parseInt(hex.slice(2, 4), 16) + amount));
  const b = Math.max(0, Math.min(255, parseInt(hex.slice(4, 6), 16) + amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
